import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ModifierRowItem from './ModifierRowItem';
import { useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getObjectValue, mapStringCartToObject } from '@utility/utills';
import { urlSearchParams } from '@utility/utills';
import cn from 'classnames';
import _ from 'lodash';
import NotesIcon from '@assets/images/NotesIcon';
import {
  updateExcludeModifier,
  applyModFunction,
  calculateModifierAmount,
} from '@containers/MenuOrderV2/MenuContent/MenuContentItems/MenuItem/MenuItemHelper';
import {
  pushModifierToCount,
  editModifiers,
  setSelectedModifierPath,
} from '@reducers/orders';
import { LABEL } from '@veriables';
import styles from './OrderItemModifierCart.module.scss';
import FullWidthModal from '@components/common/Modal/FullWidthModal';
import SpecialInstruction from '@containers/OrderItemDetail/SpecialInstructionRevamp';
import { computeHoldTime } from '@utility/helperFunction';
import { HoldController } from '@components/HoldController';

// Seeds the per-modifier note map from the existing cart item's modifiers
// (edit mode) so previously-entered notes show up when re-opening "Modify".
const getInitialModifierNote = (cartList, currentInternalId) => {
  if (!currentInternalId) return {};
  const cartItem = (cartList || []).find(
    c => c.internalId === Number(currentInternalId)
  );
  if (!Array.isArray(cartItem?.modifiers)) return {};
  const result = {};
  cartItem.modifiers.forEach(m => {
    const noteVal = m?.note || m?.comment;
    if (noteVal) {
      result[`${m.modifierId}_${m.modifierGroupId}`] = {
        note: noteVal,
        modifierId: m.modifierId,
        modifierGroupId: m.modifierGroupId,
        open: false,
      };
    }
  });
  return result;
};

const OrderItemModifierCart = ({
  deviceInfo,
  modifierData,
  menuCard,
  menusList,
  menuIndex,
  menuItemsHashTable,
  menusHashTable,
  modifiersHashTable,
  modifierGroupsHashTable,
  nestedModifierArray,
  pushModifierToCount,
  serverAppTheme,
  isNestedModifier,
  auth,
  modifierLevelData,
  editModifiers,
  note = '',
  seat,
  setSelectedModifierPath,
  selectedModifierPath,
  cart,
  globalSelectedModifierId,
}) => {
  const { menuItemId, menuTypeId, internalId } = useParams();
  const parentMenuItemInfo = useMemo(() => {
    const pid = urlSearchParams('parentMenuItemId');
    return pid ? menuCard[menuTypeId][Number(pid)] : null;
  }, [menuTypeId]);
  const itemInfo = menuCard[menuTypeId][menuItemId];
  const itemDetailsInfo = parentMenuItemInfo || itemInfo;
  const ItemDetails = itemDetailsInfo?.menuItem;
  const minQuantity = ItemDetails?.minOrderQuantity;
  const [cartQuantity] = useState(minQuantity || 1);
  const [selectedModifiers, setSelectedModifiers] = useState(null);
  const activeLevel = nestedModifierArray.length;


  const [modifierNote, setModifierNote] = useState(() =>
    getInitialModifierNote(cart, internalId)
  );
  // Which modifier's note modal is currently open
  const [activeNoteModifier, setActiveNoteModifier] = useState(null);
  // For SpecialInstruction keyboard detection
  const [browserOrtablet, setBrowserOrtablet] = useState(true);

  // ── Hold state ─────────────────────────────────────────────────────────────
  const [holdMinutes, setHoldMinutes] = useState(0);
  const [holdDeleted, setHoldDeleted] = useState(false);

  // sendDate for the current cart item (edit mode only)
  const sendDate = useMemo(() => {
    if (!internalId) return null;
    const cartItem = cart?.find(c => c.internalId === Number(internalId));
    if (!cartItem?.id) return null;
    return seat?.orders?.[0]?.items?.find(i => i.id === cartItem.id)?.sendDate ?? null;
  }, [internalId, cart, seat]);

  // Sync computed holdTime to sessionStorage so OrderItemDetailHandler can read it.
  useEffect(() => {
    const holdTimeVal = computeHoldTime(Boolean(sendDate), holdMinutes, holdDeleted);
    if (holdTimeVal !== undefined) {
      window.sessionStorage.setItem('pendingHoldTime', JSON.stringify(holdTimeVal));
    } else {
      window.sessionStorage.removeItem('pendingHoldTime');
    }
  }, [holdMinutes, holdDeleted, sendDate]);

  // Clear sessionStorage on unmount.
  useEffect(() => {
    return () => window.sessionStorage.removeItem('pendingHoldTime');
  }, []);

  // Sync per-modifier notes to sessionStorage so OrderItemDetailHandler can
  // merge them onto the modifier objects when the item is added/updated.
  useEffect(() => {
    window.sessionStorage.setItem('pendingModifierNote', JSON.stringify(modifierNote));
  }, [modifierNote]);

  useEffect(() => {
    return () => window.sessionStorage.removeItem('pendingModifierNote');
  }, []);

  // Reset hold UI when navigating to a different item.
  useEffect(() => {
    setHoldMinutes(0);
    setHoldDeleted(false);
  }, [internalId, menuItemId]);
  // ── End Hold state ──────────────────────────────────────────────────────────

  // Whether modifier memos are enabled (device setting)
  const modifierMemosEnabled = useCallback(() => {
    return (
      deviceInfo?.deviceGroup?.serverAppSettings?.disableModifierMemos !== true
    );
  }, []);

  // Whether the global modifier panel button should be available
  const isGlobalModFuncDisplay = useMemo(
    () =>
      menuCard?.[menuTypeId]?.[menuItemId]?.menuItem?.modifierGroupLinks
        ?.length > 0 &&
      menusList?.[menuIndex]?.globalModifierGroupIds?.length > 0,
    [menuCard, menuTypeId, menuItemId, menusList, menuIndex]
  );

  const modifierSortOrderMap = useMemo(() => {
    const map = {};
    const links =
      menuItemsHashTable[Number(menuItemId)]?.modifierGroupLinks || [];
    links.forEach(link => {
      map[link.modifierGroupId] = link.sortOrder;
    });
    return map;
  }, [menuItemsHashTable, menuItemId]);

  const onChangeModifier = useCallback(() => {
    const filteredData = modifierData.filter(e => e);
    const sortedModifierData = _.sortBy(
      filteredData,
      o => modifierSortOrderMap[Number(o.split('|')[0].split('-')[1])] ?? 200
    );

    // Linked modifier entries reference menu items (not modifiers) — mapStringCartToObject
    // cannot resolve them via modifiersHashTable, so we separate them here for display.
    const linkedModifierEntries = sortedModifierData.filter(
      d => d && modifierGroupsHashTable[Number(d.split('|')[0].split('-')[1])]?.linked && !d.includes('|')
    );
    const nonLinkedModifierData = linkedModifierEntries.length > 0
      ? sortedModifierData.filter(
          d => d && !modifierGroupsHashTable[Number(d.split('|')[0].split('-')[1])]?.linked
        )
      : sortedModifierData;
    const dataToSend = {
      quantity: cartQuantity,
      price: itemInfo.price,
      menuItemId: itemInfo.menuItemId,
      menuTypeId: itemInfo.menuTypeId,
      modifiers: nonLinkedModifierData,
      menuItemName: (itemInfo?.menuItem || {})?.name,
      favorite: itemInfo?.menuItem?.favorite,
    };
    let cartItem = updateExcludeModifier({
      cartItemsList: [dataToSend],
      menuItemsHashTable,
      modifierGroupsHashTable,
      modifiersHashTable,
    });
    cartItem = applyModFunction({
      cartItemsList: cartItem,
      modifierGroupsHashTable,
    });
    const modifierObject = getObjectValue(
      () =>
        mapStringCartToObject(
          cartItem,
          [],
          modifiersHashTable,
          modifierGroupsHashTable
        )[0].modifiers,
      []
    );
    modifierObject?.forEach(mod => {
      const modKey = `${mod.modifierId}-${mod.modifierGroupId}`;
      const nestedEntries = sortedModifierData.filter(
        d => d.split('|')[0] === modKey && d.includes('|')
      );
      const plainEntries = sortedModifierData.filter(
        d => d === modKey
      );
      // For plain (non-nested) entries: quantity = how many times this modifier was added.
      // For nested-only entries (e.g. a pizza in a bundle that only exists as a parent prefix):
      // quantity should be 1 per unique pizza instance, NOT the count of its child toppings.
      const userSelectedCount = plainEntries.length > 0
        ? plainEntries.length
        : nestedEntries.length > 0 ? 1 : 0;
      if (userSelectedCount > 0) {
        mod.quantity = userSelectedCount;
      }
    });
    const filteredModifiers = modifierObject?.filter(
      (modifier, index, self) => {
        const hasModifiers =
          modifier?.modifiers && modifier.modifiers.length > 0;
        const isUniqueModifierName =
          self.findIndex(
            item => item?.modifierName === modifier?.modifierName
          ) === index;
        return hasModifiers || isUniqueModifierName;
      }
    );

    // Build display entries for linked menu items using menuItemsHashTable
    const linkedDisplayEntries = linkedModifierEntries.map(d => {
      const linkedMenuItemId = Number(d.split('|')[0].split('-')[0]);
      const modifierGroupId = Number(d.split('|')[0].split('-')[1]);
      const linkedItemInfo = menuItemsHashTable[linkedMenuItemId];
      return {
        modifierId: linkedMenuItemId,
        modifierGroupId,
        modifierName: linkedItemInfo?.name || '',
        quantity: 1,
        price: linkedItemInfo?.price || 0,
        modifiers: [],
        isLinked: true,
      };
    });

    const allFilteredModifiers = [...filteredModifiers, ...linkedDisplayEntries];

    const arraymove = (arr, nonGlobalArr, toIndex) => {
      nonGlobalArr.forEach(e => {
        const element = arr.find(g => g.modifierGroupId === e.modifierGroupId);
        const fromIndex = arr.findIndex(
          f => f.modifierGroupId === element.modifierGroupId
        );
        arr.splice(fromIndex, 1);
        arr.splice(toIndex, 0, element);
      });
    };

    const groupedFilterModifiers = allFilteredModifiers.reduce((curr, modi) => {
      const groupName =
        modifierGroupsHashTable[Number(modi?.modifierGroupId)]?.name;
      if (!curr[groupName]) {
        curr[groupName] = [];
      }
      curr[groupName].push(modi);
      return curr;
    }, {});
    if (
      allFilteredModifiers.length > 0 &&
      allFilteredModifiers.filter(e =>
        menusList[menuIndex]?.globalModifierGroupIds?.includes(
          e.modifierGroupId
        )
      ).length > 0
    ) {
      arraymove(
        allFilteredModifiers,
        allFilteredModifiers.filter(
          e =>
            !menusList[menuIndex]?.globalModifierGroupIds?.includes(
              e.modifierGroupId
            )
        ),
        allFilteredModifiers.length - 1
      );
    }
    dataToSend.modifierObject = allFilteredModifiers;
    const modiferPrice = calculateModifierAmount({
      modifierData: [],
      modifierGroupsHashTable,
    });
    const requestData = {
      ...dataToSend,
      note: note,
      pending: true,
      price: Number(dataToSend.price) + modiferPrice,
      menuScreenId: Number(menuTypeId),
      ageRestricted: menusHashTable[Number(menuTypeId)].ageRestricted,
      internalId: new Date().getTime(),
      menuId: menusList[menuIndex].id,
      storeId: deviceInfo.storeId,
      modifiers: allFilteredModifiers,
      groupedFilterModifiers,
      modifierData,
    };
    // Avoid JSON.stringify on every click — only needed when cart is committed
    requestData.modifierJson = null;
    setSelectedModifiers(requestData);
  }, [
    modifierData,
    modifierSortOrderMap,
    cartQuantity,
    itemInfo,
    menuItemsHashTable,
    modifierGroupsHashTable,
    modifiersHashTable,
    menusList,
    menuIndex,
    note,
    menuTypeId,
    menusHashTable,
    deviceInfo,
  ]); // menuItemsHashTable already included — needed for linked display entries

  useEffect(() => {
    onChangeModifier();
  }, [onChangeModifier]);

  const levelGroupTotals = useMemo(() => {
    const totals = {};

    const traverse = (items = [], level = 0) => {
      items.forEach(item => {
        if (level === activeLevel) {
          const groupId = item.modifierGroupId;

          if (!totals[groupId]) {
            totals[groupId] = 0;
          }

          totals[groupId] += item.quantity || 0;
        }

        if (item.modifiers?.length > 0) {
          traverse(item.modifiers, level + 1);
        }
      });
    };

    const allModifiers = Object.values(
      selectedModifiers?.groupedFilterModifiers ?? {}
    ).flat();

    traverse(allModifiers, 0);

    return totals;
  }, [selectedModifiers, activeLevel]);

  const modifierEdit = useCallback(modifier => {
    editModifiers({
      modifier: modifier.modifierId,
      modifierGroupId: modifier.modifierGroupId,
      modifierName: modifier.modifierName,
    });
  }, [editModifiers]);

  // Computes total selected weight per weight-based modifier group at the active level.
  const levelGroupWeightTotals = useMemo(() => {
    const totals = {};

    const traverse = (items = [], level = 0) => {
      items.forEach(item => {
        if (level === activeLevel) {
          const groupId = item.modifierGroupId;
          const group = modifierGroupsHashTable[groupId];

          if (group?.isWeight) {
            if (!totals[groupId]) totals[groupId] = 0;
            const modLink = group.modifierLinks?.find(
              l => Number(l.modifierId) === Number(item.modifierId)
            );
            const weight = modLink?.weight ?? 0;
            totals[groupId] += (item.quantity || 1) * weight;
          }
        }

        if (item.modifiers?.length > 0) {
          traverse(item.modifiers, level + 1);
        }
      });
    };

    const allModifiers = Object.values(
      selectedModifiers?.groupedFilterModifiers ?? {}
    ).flat();

    traverse(allModifiers, 0);

    return totals;
  }, [selectedModifiers, activeLevel, modifierGroupsHashTable]);

  // Save a note for a specific modifier
  const handleNote = useCallback(
    (value, id, open, mGId) => {
      const temp = { ...modifierNote };
      const key = `${id}_${mGId}`;
      const prevNote = (id && mGId && modifierNote[key]?.note) || undefined;
      temp[key] = {
        note: value ?? prevNote,
        modifierId: id,
        modifierGroupId: mGId,
        open,
      };
      setModifierNote(temp);
    },
    [modifierNote]
  );

  // Get stored note text by `${modifierId}_${modifierGroupId}` key
  const getModifierNoteById = useCallback(
    id => {
      if (id?.length > 0 && modifierNote[id]?.note?.length > 0) {
        return modifierNote[id]?.note;
      }
      return '';
    },
    [modifierNote]
  );

  // Set the parent modifier path for global modifier nesting.
  // Navigation to GlobalModifierPanel is triggered separately by the
  // global modifier group pill buttons in OrderItemHeader.
  const handleGlobalModButtonClick = useCallback(
    (modifierId, modifierGroupId) => {
      const path = `${modifierId}-${modifierGroupId}`;
      if (selectedModifierPath === path) {
        setSelectedModifierPath(null);
      } else {
        setSelectedModifierPath(path);
      }
    },
    [setSelectedModifierPath, selectedModifierPath]
  );

  const renderModifiers = useCallback(
    (items = [], level = 0) => {
      return items.map((item, idx) => {
        const noteKey = `${item.modifierId}_${item.modifierGroupId}`;
        return (
          <ModifierRowItem
            key={`${item.modifierId}-${item.modifierGroupId ?? ''}-${idx}`}
            item={item}
            level={level}
            activeLevel={activeLevel}
            items={items}
            levelGroupTotals={levelGroupTotals}
            levelGroupWeightTotals={levelGroupWeightTotals}
            modifiersHashTable={modifiersHashTable}
            modifierGroupsHashTable={modifierGroupsHashTable}
            auth={auth}
            serverAppTheme={serverAppTheme}
            menusList={menusList}
            menuIndex={menuIndex}
            modifierLevelData={modifierLevelData}
            isNestedModifier={isNestedModifier}
            isGlobalModFuncDisplay={isGlobalModFuncDisplay}
            modifierMemosEnabled={modifierMemosEnabled}
            noteText={getModifierNoteById(noteKey)}
            nestedModifierArray={nestedModifierArray}
            pushModifierToCount={pushModifierToCount}
            onModifierEdit={modifierEdit}
            onNoteOpen={setActiveNoteModifier}
            handleGlobalModButtonClick={handleGlobalModButtonClick}
            selectedModifierPath={selectedModifierPath}
            renderChildren={renderModifiers}
          />
        );
      });
    },
    [
      activeLevel,
      levelGroupTotals,
      levelGroupWeightTotals,
      modifierGroupsHashTable,
      modifiersHashTable,
      auth,
      nestedModifierArray,
      serverAppTheme,
      pushModifierToCount,
      modifierLevelData,
      isNestedModifier,
      isGlobalModFuncDisplay,
      modifierMemosEnabled,
      getModifierNoteById,
      handleGlobalModButtonClick,
      selectedModifierPath,
      menusList,
      menuIndex,
      modifierEdit,
    ]
  );

  // Note value for the currently-open note modal
  const activeNoteValue = activeNoteModifier
    ? modifierNote[
        `${activeNoteModifier.modifierId}_${activeNoteModifier.modifierGroupId}`
      ]?.note || ''
    : '';

  const enableItemHold = useMemo(
    () =>
      getObjectValue(
        () =>
          deviceInfo?.deviceGroup &&
          deviceInfo?.deviceGroup?.serverAppSettings?.enableItemHold,
        false
      ),
    [deviceInfo]
  );

  const showHoldControl = !globalSelectedModifierId && enableItemHold;
  const hasHoldApplied = Boolean(sendDate);

  return (
    <div
      className={cn(
        'w-100 d-flex flex-column h-100',
        styles.modifiercartContainer
      )}
    >
      {note && (
        <div
          className="row mx-2 py-2"
          style={{
            borderBottom: `1px solid ${serverAppTheme?.modifier_separator}`,
          }}
        >
          <span className="col-1 px-0 d-flex align-items-center">
            <NotesIcon />
          </span>
          <span
            style={{
              color: serverAppTheme?.primary_text,
            }}
            className="col-11 p-0 pl-2 fs-12 d-flex align-items-center"
          >
            {note}
          </span>
        </div>
      )}
      <div className="flex-grow-1 overflowY">
        {Object.entries(selectedModifiers?.groupedFilterModifiers ?? {}).map(
          ([groupName, modifiers]) => (
            <div className="col-12 px-0" key={groupName}>
              {/* Modifiers */}
              <div className="col-12 px-0">
                {renderModifiers(modifiers, 0)}
              </div>
            </div>
          )
        )}
      </div>

      {/* Hold control footer */}
      {/* {showHoldControl && (
        <div className={styles.holdFooter}>
          <span className={styles.holdLabel}>Hold:</span>
          <HoldController
            isEditMode={Boolean(internalId)}
            hasHoldApplied={hasHoldApplied}
            holdMinutes={holdMinutes}
            setHoldMinutes={setHoldMinutes}
            holdDeleted={holdDeleted}
            setHoldDeleted={setHoldDeleted}
            sendDate={sendDate}
            serverAppTheme={serverAppTheme}
            dropdownOpensDown={false}
            isFromHoldScreen={false}
          />
        </div>
      )} */}

      {/* Per-modifier note modal */}
      {activeNoteModifier && (
        <FullWidthModal
          isOpen={!!activeNoteModifier}
          customClass2="w-100 h-100"
          noBackground
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            pointerEvents: 'auto',
            backgroundColor: serverAppTheme?.popUp_bg,
            backgroundClip: 'padding-box',
            height: '33rem',
            border: serverAppTheme?.popupBorder,
            boxShadow: serverAppTheme?.popUp_box_shadow,
            color: serverAppTheme?.primary_text,
          }}
        >
          <div
            className="col-12 px-0 m-auto justify-content-center align-items-center position-relative text-center"
            style={{ color: serverAppTheme?.primary_text }}
          >
            <SpecialInstruction
              name="modifierNote"
              type="text"
              isOpen={!!activeNoteModifier}
              specialValue={activeNoteValue}
              cancelModal={() => setActiveNoteModifier(null)}
              onSubmit={value => {
                handleNote(
                  value,
                  activeNoteModifier.modifierId,
                  false,
                  activeNoteModifier.modifierGroupId
                );
                setActiveNoteModifier(null);
              }}
              isLandScape={true}
              browserOrtablet={browserOrtablet}
              setBrowserOrtablet={setBrowserOrtablet}
              serverAppTheme={serverAppTheme}
              setOpenModal={() => setActiveNoteModifier(null)}
              seat={seat}
              Item={false}
              maxLength={20}
              placeholder={LABEL().ADD_A_NOTE}
            />
          </div>
        </FullWidthModal>
      )}
    </div>
  );
};

const mapStateToProps = ({ orders, theme, oauth }) => ({
  modifierData: orders.modifierData,
  modifiersHashTable: orders.modifiersHashTable,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  menusHashTable: orders.menusHashTable,
  menuItemsHashTable: orders.menuItemsHashTable,
  menuCard: orders.menuCardList,
  menusList: orders.menusList,
  menuIndex: orders.menuIndex,
  nestedModifierArray: orders.nestedModifierArray,
  isNestedModifier: orders.isNestedModifier,
  serverAppTheme: theme.serverAppTheme,
  deviceInfo: oauth.deviceInformation,
  auth: oauth.authInfo,
  modifierLevelData: orders.modifierLevelData,
  seat: orders.seats,
  selectedModifierPath: orders.selectedModifierPath,
  cart: orders.cart,
  globalSelectedModifierId: orders.globalSelectedModifierId,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      pushModifierToCount,
      editModifiers,
      setSelectedModifierPath,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(OrderItemModifierCart));
